import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatusBadge } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import { fmtDate } from "@/lib/dates";
import { DatePicker } from "@/components/DatePicker";
const HALF_DAY_TYPES = new Set(["Sick Leave", "Casual Leave"]);
const UNPAID_TYPES = new Set(["Leave Without Pay"]);
const EMPTY_FORM = { leave_type: "", start_date: "", end_date: "", reason: "", half_day: false, half_day_period: "First Half" };

export default function LeavePage() {
  const { user } = useAuth();
  const [balance, setBalance] = useState({});
  const [requests, setRequests] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const isApprover = user.role === "Admin";
  const halfDayEligible = HALF_DAY_TYPES.has(form.leave_type) && form.start_date && form.start_date === form.end_date;

  const load = async () => {
    const [b, r, t] = await Promise.all([
      api.get("/leave/balance"),
      api.get("/leave"),
      api.get("/leave/types"),
    ]);
    setBalance(b.data || {});
    setRequests(r.data || []);
    setTypes(t.data || []);
  };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    try {
      const payload = { ...form };
      if (!halfDayEligible) {
        payload.half_day = false;
        payload.half_day_period = null;
      }
      await api.post("/leave/apply", payload);
      toast.success("Leave applied");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(parseApiError(e, "Failed")); }
  };

  const decide = async (id, status) => {
    try {
      await api.patch(`/leave/${id}`, { status, note: "" });
      toast.success(status);
      load();
    } catch { toast.error("Failed"); }
  };

  const cancel = async (lv) => {
    const isApproved = lv.status === "Approved";
    const msg = isApproved
      ? "Cancel this approved leave? Your balance will be restored."
      : "Cancel this leave request?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/leave/${lv.id}`);
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  const myRequests = requests.filter((r) => r.user_id === user.user_id);
  const teamRequests = requests.filter((r) => r.user_id !== user.user_id);
  const today = new Date().toISOString().slice(0, 10);
  const canCancelMine = (r) =>
    (r.status === "Pending") ||
    (r.status === "Approved" && (r.start_date || "") > today);

  return (
    <div className="min-h-screen bg-white" data-testid="leave-page">
      <PageHeader
        title="Leave"
        subtitle="Balances & Requests"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(EMPTY_FORM); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-apply-leave">
                <Plus size={14} className="mr-1.5" /> Apply for Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Apply for Leave</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Leave Type *</Label>
                  <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v, half_day: false })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="select-leave-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => {
                        const bal = balance[t] ?? 0;
                        const isUnpaid = UNPAID_TYPES.has(t);
                        // Unpaid types (LWP) are always selectable. Paid types
                        // are disabled when the balance is exhausted.
                        const disabled = !isUnpaid && bal <= 0;
                        return (
                          <SelectItem key={t} value={t} disabled={disabled} data-testid={`leave-type-option-${t.replace(/\s+/g, "-").toLowerCase()}`}>
                            {t}{" "}
                            <span className="font-mono-data text-slate-400">
                              {isUnpaid
                                ? "(unpaid — no balance needed)"
                                : `(bal: ${bal}${disabled ? " — exhausted" : ""})`}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Start Date *</Label>
                    <div className="mt-1"><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="input-leave-start" /></div>
                  </div>
                  <div>
                    <Label className="text-xs">End Date *</Label>
                    <div className="mt-1"><DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="input-leave-end" /></div>
                  </div>
                </div>

                {/* Half-day section — only for Sick / Casual single-day leaves */}
                {halfDayEligible && (
                  <div className="border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="half-day-section">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.half_day}
                        onChange={(e) => setForm({ ...form, half_day: e.target.checked })}
                        data-testid="checkbox-half-day"
                      />
                      <span className="text-xs font-medium">Apply as half-day (0.5)</span>
                    </label>
                    {form.half_day && (
                      <div className="mt-2 flex items-center gap-3 pl-6">
                        {["First Half", "Second Half"].map((p) => (
                          <label key={p} className="inline-flex items-center gap-1.5 cursor-pointer text-xs">
                            <input
                              type="radio"
                              name="half_day_period"
                              checked={form.half_day_period === p}
                              onChange={() => setForm({ ...form, half_day_period: p })}
                              data-testid={`radio-${p.replace(" ", "-").toLowerCase()}`}
                            />
                            {p}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-xs">Reason</Label>
                  <Textarea className="mt-1 rounded-sm" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="input-leave-reason" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={apply} data-testid="btn-submit-leave">Apply</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-4 sm:px-8 py-6">
        {/* Balances — skip Leave Without Pay (no quota, always available) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Object.entries(balance)
            .filter(([k]) => !UNPAID_TYPES.has(k))
            .map(([k, v]) => (
              <div key={k} className="border border-slate-200 px-4 py-3" data-testid={`balance-${k.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="gc-label">{k}</div>
                <div className="mt-1 text-3xl font-mono-data font-medium text-[#0a2540]">{v}</div>
                <div className="text-[10px] text-slate-400">days available</div>
              </div>
          ))}
        </div>

        <Tabs defaultValue="mine">
          <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0">
            <TabsTrigger value="mine" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-4">My Requests ({myRequests.length})</TabsTrigger>
            {isApprover && <TabsTrigger value="team" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0a2540] data-[state=active]:shadow-none pb-2 px-4">Team Requests ({teamRequests.length})</TabsTrigger>}
          </TabsList>

          <TabsContent value="mine" className="pt-5">
            <LeaveTable items={myRequests} canDecide={false} onCancel={cancel} canCancelRow={canCancelMine} currentUserId={user.user_id} />
          </TabsContent>
          {isApprover && (
            <TabsContent value="team" className="pt-5">
              <LeaveTable items={teamRequests} canDecide={true} onDecide={decide} onCancel={cancel} canCancelRow={(r) => r.status === "Pending" || r.status === "Approved"} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

function LeaveTable({ items, canDecide, onDecide, onCancel, canCancelRow, currentUserId }) {
  return (
    <div className="border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-4 py-2.5">Employee</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-left px-4 py-2.5">From</th>
            <th className="text-left px-4 py-2.5">To</th>
            <th className="text-left px-4 py-2.5">Days</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5">Reason</th>
            <th className="px-4 py-2.5 w-24"></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-slate-400">No requests</td></tr>}
          {items.map((r) => (
            <tr key={r.id} className="border-b border-slate-100" data-testid={`leave-row-${r.id}`}>
              <td className="px-4 py-2.5 text-xs">{r.user_name}</td>
              <td className="px-4 py-2.5 text-xs">
                {r.leave_type}
                {r.half_day && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 text-[10px] font-medium">
                    Half · {r.half_day_period}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 font-mono-data text-xs">{fmtDate(r.start_date)}</td>
              <td className="px-4 py-2.5 font-mono-data text-xs">{fmtDate(r.end_date)}</td>
              <td className="px-4 py-2.5 font-mono-data text-xs">{r.days}</td>
              <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-2.5 text-xs text-slate-600 max-w-xs truncate">{r.reason}</td>
              <td className="px-4 py-2.5 text-right">
                <div className="inline-flex gap-1">
                  {canDecide && r.status === "Pending" && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-green-700 border-green-300 hover:bg-green-50" onClick={() => onDecide(r.id, "Approved")} data-testid={`btn-approve-${r.id}`}>
                        <Check size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-red-700 border-red-300 hover:bg-red-50" onClick={() => onDecide(r.id, "Rejected")} data-testid={`btn-reject-${r.id}`}>
                        <X size={12} />
                      </Button>
                    </>
                  )}
                  {onCancel && canCancelRow && canCancelRow(r) && (canDecide || r.user_id === currentUserId) && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onCancel(r)} data-testid={`btn-cancel-${r.id}`}>
                      Cancel
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
